import { toast } from '@cuspar/ui';
import { YWT_PUBLIC_CONFIG } from '@cuspar/ywt.ch.config/public';
import * as Sentry from '@sentry/react';
import type { AxiosResponse } from 'axios';
import axios from 'axios';

export type IAxiosResponse<T> =
  | {
      success: true;
      data: T;
    }
  | {
      success: false;
      reason: string;
    };

axios.defaults.withCredentials = true;

type WrapAxiosOptions = {
  showError?: boolean;
  captureServerErrors?: boolean;
};

export async function wrapAxios<T>(axiosPromise: () => Promise<AxiosResponse<T>>, options: boolean | WrapAxiosOptions = true): Promise<IAxiosResponse<T>> {
  const showError = typeof options === 'boolean' ? options : options.showError !== false;
  const captureServerErrors = typeof options === 'boolean' ? true : options.captureServerErrors !== false;

  try {
    const response = await axiosPromise();
    return { success: true, data: response.data };
  } catch (error) {
    let errorMessage: string;
    let errorStatus: number | undefined;
    if (axios.isAxiosError(error)) {
      errorStatus = error.response?.status;
      if (errorStatus === 429) errorMessage = 'Too many requests. Please wait a moment and try again.';
      else if (error.response?.data.message) {
        const msg = error.response.data.message;
        errorMessage = (typeof msg === 'string' ? msg : (msg.message ?? msg)).toString();
      } else errorMessage = `Error: ${error.status ?? error.code ?? error.name} - ${error.message}`;
    } else if (error instanceof Error) {
      errorMessage = `Error: ${error.message}`;
    } else errorMessage = 'Unknown Error';
    // show error message
    if (showError) toast.error(errorMessage);

    // Forward server-side bugs (5xx) to Sentry. 4xx are user errors and stay client-side.
    if (captureServerErrors && errorStatus !== undefined && errorStatus >= 500) {
      Sentry.captureException(error, { tags: { source: 'wrapAxios', status: errorStatus } });
    }

    return { success: false, reason: errorMessage };
  }
}

axios.defaults.baseURL = YWT_PUBLIC_CONFIG.backend.url;
